import { motion } from 'framer-motion';
import { Film, Tv } from 'lucide-react';
import type { TraktUser, UserStats } from '../../../services/traktApi';
import { TraktLogo } from './TraktLogo';
import { LABELS } from '../../../data/labels';

export const TraktProfileCard = ({ user, stats, loading, labels }: {
  user: TraktUser | null;
  stats: UserStats | null;
  loading: boolean;
  labels: typeof LABELS['fr'];
}) => {
  if (loading) {
    return (
      <div className="card card-hover p-6 h-full border-l-4 border-l-[#ed1c24]">
        <div className="animate-pulse space-y-4">
          <div className="h-16 w-16 bg-gray-200 rounded-full" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  const formatNumber = (num: number) => new Intl.NumberFormat('fr-FR').format(num);
  const formatHours = (minutes: number) => Math.round(minutes / 60);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card card-hover p-6 h-full border-l-4 border-l-[#ed1c24]"
    >
      {/* Header avec avatar et infos */}
      <div className="flex items-start gap-4 mb-4">
        {/* Avatar avec logo Trakt intégré */}
        <div className="relative flex-shrink-0">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#ed1c24] to-[#ff4757] flex items-center justify-center text-white text-xl font-bold shadow-lg">
            {user.name.charAt(0).toUpperCase()}
          </div>
          {/* Petit logo Trakt en badge */}
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border border-[#ed1c24]">
            <TraktLogo className="w-4 h-4 text-[#ed1c24]" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-[#1d1d1f] text-lg flex items-center gap-2 flex-wrap">
            {user.name}
            {user.vip && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-[#ed1c24] text-white">
                VIP
              </span>
            )}
          </h3>
          <p className="text-sm text-[#ed1c24] font-medium">@{user.username}</p>

          {/* Lien Trakt sous le username */}
          <a
            href={`https://trakt.tv/users/${user.username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs text-[#5e5e5e] hover:text-[#ed1c24] transition-colors"
          >
            <TraktLogo className="w-3 h-3" />
            {labels.view_on_trakt}
          </a>
        </div>
      </div>

      {user.about && (
        <p className="text-sm text-[#5e5e5e] mb-4 line-clamp-2">{user.about}</p>
      )}

      {stats && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gradient-to-br from-[#ed1c24]/5 to-[#ed1c24]/10 rounded-lg p-3 border border-[#ed1c24]/10">
            <div className="flex items-center gap-2 text-[#ed1c24] mb-1">
              <Film className="w-4 h-4" />
              <span className="text-xs font-medium">{labels.movies}</span>
            </div>
            <div className="font-bold text-[#1d1d1f] text-lg">{formatNumber(stats.movies.watched)}</div>
            <div className="text-xs text-[#ed1c24]/70">{formatHours(stats.movies.minutes)}h</div>
          </div>
          <div className="bg-gradient-to-br from-[#ed1c24]/5 to-[#ed1c24]/10 rounded-lg p-3 border border-[#ed1c24]/10">
            <div className="flex items-center gap-2 text-[#ed1c24] mb-1">
              <Tv className="w-4 h-4" />
              <span className="text-xs font-medium">{labels.shows}</span>
            </div>
            <div className="font-bold text-[#1d1d1f] text-lg">{formatNumber(stats.shows.watched)}</div>
            <div className="text-xs text-[#ed1c24]/70">{formatHours(stats.episodes.minutes)}h</div>
          </div>
        </div>
      )}
    </motion.div>
  );
};
